<template>
  <div
    v-if="visible"
    class="vast-service-order-now-url-input__search-results"
  >
    <div class="vast-service-order-now-url-input__search-header">
      <p class="vast-service-order-now-url-input__search-title">
        {{ locale.urlSearchHeader }}
      </p>

      <OBtn
        class="vast-service-order-now-url-input__search-close"
        text
        height="32"
        @click="$emit('close')"
      >
        <icon-close />
      </OBtn>
    </div>

    <div
      v-if="loading"
      class="vast-service-order-now-url-input__search-status"
    >
      {{ locale.urlSearchLoading }}
    </div>

    <div
      v-else-if="error"
      class="vast-service-order-now-url-input__search-status"
    >
      {{ locale.urlSearchError }}
    </div>

    <div
      v-else-if="!results.length"
      class="vast-service-order-now-url-input__search-status"
    >
      {{ locale.urlSearchEmpty }}
    </div>

    <div
      v-else
      class="vast-service-order-now-url-input__search-list"
    >
      <div
        v-for="item in results"
        :key="item.id"
        class="vast-service-order-now-url-input__search-item"
        :class="{
          'vast-service-order-now-url-input__search-item--active':
            selectedUrl === item.url
        }"
        @click="$emit('select', item)"
      >
        <div class="vast-service-order-now-url-input__search-item-left">
          <img
            :src="item.image"
            alt=""
            class="vast-service-order-now-url-input__search-image"
          >
        </div>

        <div class="vast-service-order-now-url-input__search-item-right">
          <p class="vast-service-order-now-url-input__search-name">
            {{ item.title }}
          </p>
          <p class="vast-service-order-now-url-input__search-description">
            {{ item.description }}
          </p>
          <span
            class="vast-service-order-now-url-input__search-type"
            :class="resolveType(item.type)"
          >
            {{ resolveType(item.type) }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import IconClose from '@/components/icons/IconClose.vue';

export default {
  name: 'YoutubeSearchResults',
  components: {
    IconClose,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    error: {
      type: Boolean,
      default: false,
    },
    results: {
      type: Array,
      default: () => [],
    },
    selectedUrl: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      types: {
        youtubeVideo: 'video',
        youtubeChannel: 'channel',
      },
    };
  },
  computed: {
    ...mapGetters('config', [
      'locale',
    ]),
  },
  methods: {
    normalizeType(type) {
      return String(type || '').replace(/_([a-z])/g, (_, c) => c.toUpperCase());
    },
    resolveType(type) {
      const key = this.normalizeType(type);
      return this.types[key] ?? type ?? '';
    },
  },
  emits: ['select', 'close'],
};
</script>

<style lang="scss" scoped>
.vast-service-order-now-url-input__search-results {
  position: absolute;
  z-index: 10;
  top: 100%;
  left: 0;
  right: 0;
  margin-top: 8px;
  max-height: 280px;
  overflow-y: auto;
  background: #ffffff;
  border-radius: 8px;
  box-shadow: 0 12px 35px rgba(0, 0, 0, 0.15);
  box-sizing: border-box;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: #f4f4f5;
  }

  &::-webkit-scrollbar-thumb {
    background-color: var(--selected-elements-color);
    border-radius: 4px;
  }

  @media (max-width: 768px) {
    max-height: 240px;
  }
}

.vast-service-order-now-url-input__search-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  color: var(--selected-elements-color);
  font-size: 14px;
  font-weight: 500;
}

.vast-service-order-now-url-input__search-title {
  margin: 0;
}

.vast-service-order-now-url-input__search-close {
  background: transparent;
  padding: 0;
  cursor: pointer;
  font-size: 16px;
  line-height: 1;
  color: #71717a;
  border: none !important;
  box-shadow: none !important;
}

.vast-service-order-now-url-input__search-status {
  padding: 12px 16px;
  font-size: 14px;
  color: var(--text-color);
}

.vast-service-order-now-url-input__search-list {
  padding: 4px 0 8px;
}

.vast-service-order-now-url-input__search-item {
  display: flex;
  align-items: flex-start;
  width: 100%;
  padding: 8px 32px 8px 12px;
  border: none;
  background: #ffffff;
  cursor: pointer;
  text-align: left;
  box-sizing: border-box;

  &--active,
  &:hover {
    background: #f4f4f5;
  }
}

.vast-service-order-now-url-input__search-item-left {
  flex: 0 0 auto;
  margin-right: 8px;
}

.vast-service-order-now-url-input__search-image {
  width: 72px;
  height: 40px;
  object-fit: cover;
  border-radius: 4px;
}

.vast-service-order-now-url-input__search-item-right {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.vast-service-order-now-url-input__search-name {
  margin: 0 0 4px;
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  color: #001b3a;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.vast-service-order-now-url-input__search-description {
  margin: 0;
  font-size: 10px;
  color: #71717a;
  font-style: normal;
  font-weight: 400;
  line-height: 14px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.vast-service-order-now-url-input__search-type {
  font-size: 10px;
  font-style: normal;
  font-weight: 400;
  line-height: 140%;
  text-transform: lowercase;

  &.video {
    color: var(--selected-elements-color);
  }

  &.channel {
    color: #ef4444;
  }
}
</style>
